import { KnightGame } from '../../games/knight/KnightGame.js';
import { KnightVortex } from '../../games/knight/KnightVortex.js';
import { KnightRenderer } from '../../games/knight/KnightRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Knight-specific types ──────────────────────────────────────────

export interface KnightRenderOptions {
    // Placeholder — KnightRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class KnightTutorialAdapter implements TutorialGameAdapter {
    private game: KnightGame;
    private renderer: KnightRenderer;
    private grid: Grid;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.grid = grid;
        this.game = new KnightGame(grid);
        this.renderer = new KnightRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const raw = state as string;
        this.game.reset();

        if (!raw) return;

        // Parse "segmentString+click1,click2" notation
        // Left of '+': standard segment string for deserialize()
        // Right of '+': comma-separated positions to simulate via handleClick()
        const plusIndex = raw.indexOf('+');
        let segmentPart: string;
        let clickPart: string;

        if (plusIndex >= 0) {
            segmentPart = raw.substring(0, plusIndex);
            clickPart = raw.substring(plusIndex + 1);
        } else {
            segmentPart = raw;
            clickPart = '';
        }

        if (segmentPart) {
            this.game.deserialize(segmentPart);
        }

        if (clickPart) {
            for (const posStr of clickPart.split(',')) {
                const trimmed = posStr.trim();
                if (!trimmed) continue;
                const pos = this.grid.serializer.deserializePosition(trimmed);
                this.game.handleClick(pos);
            }
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createKnightTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new KnightTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface KnightVortexTutorialState {
    knight: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class KnightVortexTutorialAdapter implements TutorialGameAdapter {
    private game: KnightVortex;
    private renderer: KnightRenderer;
    private grid: Grid;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.grid = grid;
        this.game = new KnightVortex(grid);
        this.renderer = new KnightRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as KnightVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (!s) return;

        const raw = s.knight;
        if (raw) {
            const plusIndex = raw.indexOf('+');
            let segmentPart: string;
            let clickPart: string;

            if (plusIndex >= 0) {
                segmentPart = raw.substring(0, plusIndex);
                clickPart = raw.substring(plusIndex + 1);
            } else {
                segmentPart = raw;
                clickPart = '';
            }

            if (segmentPart) {
                this.game.deserialize(segmentPart);
            }

            if (clickPart) {
                for (const posStr of clickPart.split(',')) {
                    const trimmed = posStr.trim();
                    if (!trimmed) continue;
                    const pos = this.grid.serializer.deserializePosition(trimmed);
                    this.game.handleClick(pos);
                }
            }
        }

        this.currentGateMode = s.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createKnightVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex knight tutorial: E4 ↔ B5
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 4, col: 4 }, { row: 5, col: 1 }],
    ];
    return new KnightVortexTutorialAdapter(canvas, grid, gatePairs);
}
